/**
 * Page copy for /services/qa-consulting-modernization.
 *
 * Source: page-qa-consulting.md, supplied by the client. H1 and page match, so
 * nothing is remapped except links.
 *
 * The source specifies /services/qa-consulting; the built route is
 * /services/qa-consulting-modernization, already live in the nav and footer.
 *
 * Its links use the superseded v1 slugs. Mapped to real routes where they exist:
 *   /services/test-automation-architecture → /services/qa-architecture-engineering
 *   /services/ai-testing-services          → /services/ai-driven-validation
 *   /services/managed-qa-services          → not built, held at "#"
 *   /ai-maturity-model, /ai-native-delivery-framework → not built, held at "#"
 *
 * Source note: the Clutch block "should sit between §5 and §6 on this page
 * rather than at the bottom — it's the only external verification the page has."
 * It is placed there, using the approved homepage figures.
 *
 * NO OPEN GAPS. The price slot (§8) was removed at the client's request — see
 * the note above `pricing`.
 */

export const seo = {
  title: "QA Consulting Services & Test Suite Modernization",
  description:
    "Fixed-scope QA consulting and modernization. Two weeks' assessment, then we modernize the process, tooling, suite or capability actually holding delivery back.",
} as const;

export const hero = {
  opening: [
    "Your QA practice was built for how you shipped five years ago. The process, the tooling, the suite, and the skills all made sense then. Each has been patched since. None has been rebuilt.",
    "We spend two weeks establishing which one is actually holding you back, then we modernize it. Not all four. The one that moves the rest.",
  ],
} as const;

export const outdated = {
  heading: "How do I know if our QA process is outdated?",
  lead: "It rarely announces itself as one problem. It shows up across four areas at once, at different severities:",
  areas: [
    {
      area: "Process",
      icon: "gear",
      body: "Approvals and handoffs designed for quarterly releases, still running against weekly ones",
    },
    {
      area: "Tooling",
      icon: "cube",
      body: "A stack assembled over years, poorly integrated, with data that doesn't join up",
    },
    {
      area: "Suite",
      icon: "layers",
      body: "Coverage that grew by addition, where nobody can say what's load-bearing",
    },
    {
      area: "Capability",
      icon: "users",
      body: "A team competent at what it was hired for, without room to learn what's next",
    },
  ],
  close:
    "The reason this is hard to solve internally is that all four are true simultaneously, and the loudest one is usually not the binding one. Teams fix the loudest, see no change, and conclude that quality work doesn't pay off.",
} as const;

export const process = {
  heading: "How it works",
  lead: "Diagnosis first, then modernization. Fixed scope, four to six weeks.",
  phases: [
    {
      weeks: "1–2",
      phase: "Assess",
      body: "Suite analysis, pipeline review, defect history, cycle-time data, and interviews with engineers — not just leads",
    },
    {
      weeks: "3",
      phase: "Diagnose",
      body: "Maturity placement, constraint analysis, and the prioritization call with your team",
    },
    {
      weeks: "3–5",
      phase: "Modernize",
      body: "Work on the priority areas. Working changes, not recommendations",
    },
    {
      weeks: "5–6",
      phase: "Hand over",
      body: "Enablement, standards, and a roadmap for what we didn't reach",
    },
  ],
  calloutLead: "Why two weeks of assessment before touching anything.",
  calloutBody:
    " Most QA consulting produces forty findings and no priority, which is the same as producing nothing. We're looking for the constraint — the specific thing that, if fixed, changes the others. Everything else waits, and some of it waits permanently.",
  scoringLead: "Assessment scores against the ",
  scoringLink: { label: "AI Maturity Model", href: "/services/ai-driven-validation#maturity-model-heading" },
  scoringTail:
    " across People, Processes, Platform, AI Capabilities, and Outcomes, with the evidence for each placement.",
} as const;

export const modernize = {
  heading: "What we modernize",
  lead: "All four areas are in scope. Which ones we touch depends on what the assessment finds.",
  items: [
    {
      title: "Process & practice",
      icon: "gear",
      body: [
        "Where quality sits in the delivery flow, how release decisions get made, what gets measured and who sees it. We rewrite quality gates so they block for real reasons and only real reasons — a gate everyone has learned to override is worse than no gate.",
        "Includes defect triage flow, definition of done, and the reporting that goes upward.",
      ],
    },
    {
      title: "Tooling & stack",
      icon: "cube",
      body: [
        "Consolidation and integration. The problem is usually not which tools you have but that they don't talk to each other, so nobody can answer a question that spans two of them.",
        "We connect test management, CI/CD, and defect tracking so the data joins up, and retire whatever is being paid for and not used.",
      ],
    },
    {
      title: "Legacy test suite",
      icon: "layers",
      body: [
        "Restructuring coverage across layers — moving what belongs in API and contract tests out of the browser, retiring tests that no longer earn their runtime, and establishing conventions for what gets written where.",
      ],
    },
    {
      title: "Team capability",
      icon: "users",
      body: [
        "Role-based enablement so your engineers can maintain what we build. Working sessions on the real codebase, not slide-based training. A team that can't extend the new approach will revert to the old one within two quarters.",
      ],
    },
  ],
} as const;

export const deliverables = {
  heading: "What you get",
  lead: "Real deliverables, all of them yours to keep.",
  groups: [
    {
      label: "From assessment",
      icon: "search",
      items: [
        {
          lead: "Current-state report",
          tail: " — suite runtime, flake rate, defect escape rate, coverage distribution by layer, cycle time, and cost of quality, each with its source",
        },
        {
          lead: "Maturity placement",
          tail: " across the five dimensions, with the evidence behind every level assigned",
        },
        {
          lead: "Constraint analysis",
          tail: " — one page naming what to fix first and why the alternatives are second",
        },
        {
          lead: "Prioritized modernization plan",
          tail: " with effort estimates your team can plan against",
        },
      ],
    },
    {
      label: "From modernization",
      icon: "gears",
      items: [
        {
          lead: "Revised quality process",
          tail: " — gates, triage flow, definition of done, reporting structure",
        },
        {
          lead: "Integrated toolchain",
          tail: " — connections wired, data joined, unused licences identified",
        },
        {
          lead: "Restructured suite",
          tail: " — coverage redistributed across layers, retirement list, written conventions",
        },
        { lead: "Enablement sessions", tail: " — role-based, on your codebase" },
      ],
    },
    {
      label: "At handover",
      icon: "graduation",
      items: [
        { lead: "Standards and conventions documentation", tail: "" },
        { lead: "Roadmap", tail: " for the areas we didn't reach, sequenced" },
        {
          lead: '"Not this year" list',
          tail: " — improvements that are real but shouldn't be next",
        },
      ],
    },
  ],
  close:
    "That last item is the one clients tell us they didn't expect and used most. Knowing what to ignore is worth as much as knowing what to fix.",
} as const;

export const boundary = {
  heading: "Do I need QA consulting or a test architecture rebuild?",
  lead: "Both engagements touch the test suite, so the boundary is worth stating plainly.",
  sides: [
    {
      badge: "This one is broad",
      title: "QA Consulting & Modernization",
      href: null,
      body: 'It starts from "something is wrong and I\'m not sure what," looks across process, tooling, suite, and capability, and modernizes whichever is binding. It touches the suite — restructuring coverage, retiring dead tests, setting conventions.',
    },
    {
      badge: "That one is deep",
      title: "Test Architecture & QA Platform Engineering",
      href: "/services/qa-architecture-engineering",
      body: 'It starts from "the framework is the problem," and rebuilds it — new structure, test data provisioning, pipeline orchestration, migration path.',
    },
  ],
  close:
    "If you already know the framework is the constraint, go there directly. This engagement is for when you don't yet know, or when the answer turns out to be process rather than code.",
} as const;

export const limits = {
  heading: "What this doesn't do",
  items: [
    "Four to six weeks does not modernize everything. It modernizes the constraint and gives you a sequenced route for the rest. Anyone offering full transformation in six weeks is describing a document, not a change.",
    "It also doesn't work without access. We need your engineers, your defect history, and your pipeline — not a stakeholder deck. Engagements where the team is protected from the consultants produce findings nobody acts on.",
  ],
} as const;

/**
 * §8. NO PRICE, BY DECISION — the client does not want a figure on any service
 * page. The placeholder that held the slot open was removed at their request
 * and the section is now a single column: heading, body, CTAs.
 *
 * **`heading` STILL READS "Starting from" AND THERE IS NOTHING TO START FROM.**
 * Left as the source wrote it rather than changed unasked, but it is a heading
 * promising a figure the section does not contain. The sister fixed-scope page,
 * /services/qa-architecture-engineering, had the same problem and its heading
 * became "The terms".
 *
 * If a terms table is ever wanted here, the four rows from that page transfer
 * exactly — both services are fixed scope, fixed price, four to six weeks, both
 * ending in a handover (§4's last phase is literally "Hand over"). A fifth
 * "not included" row would be redundant: §"What this doesn't do" already covers
 * it, and better.
 */
export const pricing = {
  heading: "Starting from",
  body: "Fixed scope, fixed price, four to six weeks. You know the cost before it starts, and there's no open-ended retainer to exit.",
} as const;

export const related = {
  heading: "Related",
  items: [
    {
      label: "AI Maturity Model",
      href: "/services/ai-driven-validation#maturity-model-heading",
      note: "what the assessment scores against",
    },
    {
      label: "AI-Native Delivery Framework",
      href: "/services/ai-driven-validation#framework-heading",
      note: "the wider operating model",
    },
    {
      label: "Test Architecture & QA Platform Engineering",
      href: "/services/qa-architecture-engineering",
      note: "when the framework is the constraint",
    },
    {
      label: "AI-Driven Validation",
      href: "/services/ai-driven-validation",
      note: "when test design is the bottleneck",
    },
    {
      label: "Intelligent Quality Operations",
      href: "/services/intelligent-quality-operations",
      note: "when you need the function run, not fixed",
    },
  ],
} as const;
